import { useRef, useEffect, useState } from "react";
import { ChevronRight, Lock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { DesignCategory } from "@/data/designStyles";
import { cn } from "@/lib/utils";

interface CategoryCarouselProps {
  categories: DesignCategory[];
  selectedCategory: string | null;
  onSelect: (categoryId: string) => void;
  loading?: boolean;
  /**
   * When true, the Pro-lock feature flag is enforced for this user and
   * the carousel should hint which categories have free styles available.
   */
  lockEnforced?: boolean;
  /**
   * Map of categoryId → number of free styles in that category.
   * Only consulted when `lockEnforced` is true.
   */
  freeCounts?: Record<string, number>;
}

const categoryColors = [
  "bg-emerald-200 dark:bg-emerald-900/30",
  "bg-amber-200 dark:bg-amber-900/30",
  "bg-blue-200 dark:bg-blue-900/30",
  "bg-purple-200 dark:bg-purple-900/30",
  "bg-rose-200 dark:bg-rose-900/30",
  "bg-teal-200 dark:bg-teal-900/30",
];

export const CategoryCarousel = ({
  categories,
  selectedCategory,
  onSelect,
  loading = false,
  lockEnforced = false,
  freeCounts,
}: CategoryCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const animationRef = useRef<number>();

  // Auto-scroll animation
  useEffect(() => {
    if (userInteracted || selectedCategory || loading) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const scroll = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

        // Reset to start when reaching end
        if (scrollLeft >= scrollWidth - clientWidth - 1) {
          scrollRef.current.scrollLeft = 0;
        } else {
          scrollRef.current.scrollLeft += 0.5;
        }
      }
      animationRef.current = requestAnimationFrame(scroll);
    };

    animationRef.current = requestAnimationFrame(scroll);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [userInteracted, selectedCategory, loading]);

  const handleInteraction = () => {
    setUserInteracted(true);
  };

  const handleCategorySelect = (categoryId: string) => {
    setUserInteracted(true);
    onSelect(categoryId);
  };

  if (loading) {
    return (
      <div className="flex gap-3 px-4 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="w-64 h-32 rounded-xl flex-shrink-0" />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex gap-3 px-4 overflow-x-auto scrollbar-hide p-2"
      onTouchStart={handleInteraction}
      onMouseDown={handleInteraction}
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      {categories.map((category, index) => {
        const total = category.styles.length;
        const free = freeCounts?.[category.id] ?? 0;
        const showHint = lockEnforced && free < total;
        const allLocked = lockEnforced && free === 0;

        return (
          <button
            key={category.id}
            onClick={() => handleCategorySelect(category.id)}
            className={cn(
              "flex-shrink-0 min-w-[80vw] w-[80vw] p-4 rounded-xl text-left transition-all",
              categoryColors[index % categoryColors.length],
              selectedCategory === category.id
                ? "ring-2 ring-primary ring-offset-2"
                : "hover:ring-1 hover:ring-primary/50"
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-foreground text-sm">
                    {category.name}
                  </h3>
                  <span className="text-xs font-bold bg-white px-2 py-0.5 rounded-sm whitespace-nowrap">
                    {total} styles
                  </span>
                  {allLocked && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-black/70 text-white px-2 py-0.5 rounded-sm whitespace-nowrap">
                      <Lock className="h-3 w-3" />
                      Pro
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {category.description}
                </p>
              </div>
              <ChevronRight
                className={cn(
                  "h-5 w-5 text-muted-foreground transition-transform flex-shrink-0",
                  selectedCategory === category.id && "rotate-90"
                )}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
};
